import type { ReactElement } from 'react'
import { useFormContext, useFormState } from 'react-hook-form'
import { cloneDeep } from 'lodash-es'
import { useIsMutating } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import Results2ActionBar, { type Results2ActionBarStatus } from '../../Results2ActionBar'
import { UNSAVED_BEMESSUNG_NAV_SNACKBAR_KEY } from '../constants'
import type { DesignFormRootValues } from '../types'

export type BottomActionBarProps = {
  /** Latest prop-derived defaults (`buildDefaultDesignFormElements`); required so Rückgängig matches store truth, not only the form’s first-mount snapshot. */
  undoBaselineValues: DesignFormRootValues
  disableForm?: boolean
}

export const BottomActionBar = ({
  undoBaselineValues,
  disableForm = false,
}: BottomActionBarProps): ReactElement => {
  const { closeSnackbar } = useSnackbar()
  const { reset } = useFormContext<DesignFormRootValues>()
  const { isSubmitting, dirtyFields } = useFormState<DesignFormRootValues>()

  const isDirty = Object.keys(dirtyFields).length > 0

  const isCalculatingChecks = useIsMutating(['checkSettingsRecalculation']) > 0

  const dismissUnsavedNavSnackbar = () => closeSnackbar(UNSAVED_BEMESSUNG_NAV_SNACKBAR_KEY)

  const disabled = disableForm || !isDirty || isSubmitting || isCalculatingChecks

  const status: Results2ActionBarStatus = isSubmitting ? 'saving' : isDirty ? 'dirty' : 'saved'
  const statusLabel = isSubmitting
    ? 'Wird gespeichert…'
    : isDirty
    ? 'Ungespeicherte Änderungen'
    : 'Gespeichert'

  return (
    <Results2ActionBar
      status={status}
      statusLabel={statusLabel}
      saveType="submit"
      onSave={dismissUnsavedNavSnackbar}
      saveDisabled={disabled}
      revertDisabled={disabled}
      onRevert={() => {
        reset(cloneDeep(undoBaselineValues), {
          keepErrors: false,
          keepDirty: false,
          keepDirtyValues: false,
          keepDefaultValues: false,
          keepIsSubmitted: false,
          keepTouched: false,
          keepValues: false,
        })
        dismissUnsavedNavSnackbar()
      }}
    />
  )
}
